import React, { useState } from 'react';
import { Box, Typography, Radio, Paper, Grid, TextField, Stack, IconButton } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';

const AddressOption = ({ id, title, subtitle, isSelected, onSelect }) => (
  <Paper
    elevation={0}
    onClick={() => onSelect(id)}
    sx={{
      p: 2.5,
      borderRadius: '16px',
      border: '1px solid',
      borderColor: isSelected ? '#7495f8' : '#F1F5F9',
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      mb: 2,
      cursor: 'pointer',
      bgcolor: 'white',
      transition: 'all 0.2s',
      '&:hover': { borderColor: '#7495f8', bgcolor: '#F8FAFC' }
    }}
  >
    <Radio
      checked={isSelected}
      sx={{ p: 0, color: '#E2E8F0', '&.Mui-checked': { color: '#7495f8' } }}
    />
    <Box sx={{
      width: 44,
      height: 44,
      borderRadius: '12px',
      bgcolor: '#EFF6FF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#0E2465'
    }}>
      {id === 'new' ? <AddIcon /> : <HomeIcon />}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle2" fontWeight="900" color="#0E2465">{title}</Typography>
      <Typography variant="caption" color="#737791" sx={{ fontWeight: 500, display: 'block', mt: 0.5 }}>{subtitle}</Typography>
    </Box>
  </Paper>
);

const BookingAddress = ({
  addresses,
  selectedAddressId, setSelectedAddressId,
  newAddress, setNewAddress
}) => {
  const [showNewForm, setShowNewForm] = useState(selectedAddressId === 'new');

  React.useEffect(() => {
    if (addresses.length === 0 && selectedAddressId !== 'new') {
      setSelectedAddressId('new');
      setShowNewForm(true);
    }
  }, [addresses.length, selectedAddressId, setSelectedAddressId]);

  const handleSelect = (id) => {
    setSelectedAddressId(id);
    setShowNewForm(id === 'new');
  };

  const handleNewAddressChange = (e) => {
    const { name, value } = e.target;
    const updatedAddress = {
      ...newAddress,
      [name]: value,
      // If we are changing address components manually, clear the place_id 
      // as it's no longer the exact place from Google
      place_id: ''
    };

    // Update the 'direccion' field as well to reflect manual changes
    updatedAddress.direccion = `${updatedAddress.calle} ${updatedAddress.numeroExt}, ${updatedAddress.colonia}, ${updatedAddress.municipio}, ${updatedAddress.estado}, ${updatedAddress.cp}, ${updatedAddress.pais}`;
    
    setNewAddress(updatedAddress);
  };

  const handleGoogleSelect = (dir) => {
    if (!dir || !dir.value || !dir.value.place_id) return;

    geocodeByPlaceId(dir.value.place_id).then(results => {
      if (!results || results.length === 0) return;

      let components = {};
      results[0].address_components.forEach(info => {
        if (info.types.includes("route")) components.calle = info.long_name;
        if (info.types.includes("sublocality") || info.types.includes("neighborhood")) components.colonia = info.long_name;
        if (info.types.includes("locality")) components.municipio = info.long_name;
        if (info.types.includes("administrative_area_level_1")) components.estado = info.long_name;
        if (info.types.includes("country")) components.pais = info.long_name;
        if (info.types.includes("street_number")) components.numeroExt = info.long_name;
        if (info.types.includes("postal_code")) components.cp = info.long_name;
      });

      setNewAddress({
        ...newAddress,
        calle: components.calle || '',
        colonia: components.colonia || '',
        municipio: components.municipio || '',
        estado: components.estado || '',
        pais: components.pais || '',
        numeroExt: components.numeroExt || '',
        cp: components.cp || '',
        direccion: dir.value.description || results[0].formatted_address,
        place_id: dir.value.place_id
      });
    }).catch(err => console.error("Error geocoding place:", err));
  };

  return (
    <Box sx={{ p: 4, borderRadius: '24px', bgcolor: 'white', border: '1px solid #F1F5F9' }}>
      <Typography variant="h6" fontWeight="900" color="#0E2465" sx={{ mb: 4 }}>
        Dirección de servicio
      </Typography>

      {addresses.length > 0 && (
        <>
          <Typography variant="caption" color="#737791" fontWeight="900" sx={{ display: 'block', mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            TUS DIRECCIONES
          </Typography>
          {addresses.map((addr) => (
            <AddressOption
              key={addr._id}
              id={addr._id}
              title={addr.nombre}
              subtitle={addr.direccion}
              isSelected={selectedAddressId === addr._id}
              onSelect={handleSelect}
            />
          ))}
        </>
      )}

      <Typography variant="caption" color="#737791" fontWeight="900" sx={{ display: 'block', mt: 3, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        AGREGAR NUEVA DIRECCIÓN
      </Typography>
      <AddressOption
        id="new"
        title="Nueva dirección"
        subtitle="Agregar dirección de servicio"
        isSelected={selectedAddressId === 'new'}
        onSelect={handleSelect}
      />

      {showNewForm && (
        <Box sx={{ mt: 4, p: 2, borderRadius: '20px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <InfoIcon sx={{ color: '#0E2465', fontSize: 20 }} />
            <Typography variant="caption" color="#64748B" fontWeight={500}>
              Asegúrate de proporcionar referencias claras para facilitar la llegada del proveedor.
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item size={12}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Buscar dirección *</Typography>
              <GooglePlacesAutocomplete
                autocompletionRequest={{ componentRestrictions: { country: ['mx'] } }}
                selectProps={{
                  onChange: handleGoogleSelect,
                  placeholder: "Busca tu calle y número...",
                  styles: {
                    control: (provided) => ({
                      ...provided,
                      borderRadius: '12px',
                      padding: '4px',
                      borderColor: '#E2E8F0',
                      '&:hover': { borderColor: '#7495f8' }
                    })
                  }
                }}
              />
            </Grid>

            <Grid item size={12}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Nombre de la dirección *</Typography>
              <TextField
                fullWidth name="nombre" placeholder="Ej. Casa, Oficina..."
                value={newAddress.nombre} onChange={handleNewAddressChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 8 }}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Calle *</Typography>
              <TextField
                fullWidth name="calle" value={newAddress.calle} onChange={handleNewAddressChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>
            <Grid item size={{ xs: 6, sm: 2 }}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Núm Ext. *</Typography>
              <TextField
                fullWidth name="numeroExt" value={newAddress.numeroExt} onChange={handleNewAddressChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>
            <Grid item size={{ xs: 6, sm: 2 }}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Núm Int.</Typography>
              <TextField
                fullWidth name="numeroInt" value={newAddress.numeroInt} onChange={handleNewAddressChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Colonia *</Typography>
              <TextField
                fullWidth name="colonia" value={newAddress.colonia} onChange={handleNewAddressChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>CP *</Typography>
              <TextField
                fullWidth name="cp" value={newAddress.cp} onChange={handleNewAddressChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Ciudad *</Typography>
              <TextField
                fullWidth name="municipio" value={newAddress.municipio} onChange={handleNewAddressChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Estado *</Typography>
              <TextField
                fullWidth name="estado" value={newAddress.estado} onChange={handleNewAddressChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>

            <Grid item size={12}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Referencias</Typography>
              <TextField
                fullWidth multiline rows={3} name="referencia"
                value={newAddress.referencia} onChange={handleNewAddressChange}
                placeholder="Edificio azul, junto a la farmacia..."
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 4, p: 2, borderRadius: '12px', bgcolor: 'white', border: '1px solid #E2E8F0' }}>
            <InfoIcon sx={{ color: '#FCD34D', fontSize: 20 }} />
            <Typography variant="caption" color="#64748B" fontWeight={500}>
              Asegúrate de proporcionar referencias claras para facilitar la llegada del proveedor.
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default BookingAddress;
